/**
 * Derives "is the data on screen stale" from a single MV2 QueryClient, so the
 * one shared Mv2RootFrame can show a compact notice instead of every feature
 * wiring its own.
 *
 * React Query keeps the last-known-good `data` on a query when a background
 * refetch fails — it never clears it just because the network dropped. That
 * is exactly the "preserve useful offline cached data" behavior this reads
 * off of; nothing here evicts or overrides it. What React Query does not
 * surface on its own is a compact "you're looking at old data" signal: once a
 * query has succeeded once, `status` stays `"success"` through a failed
 * background refetch, so a plain `isError` check goes quiet right when it
 * would matter most (offline, revisiting a persisted-cache page).
 */

/**
 * A query is "failing to revalidate" when its most recent fetch attempt
 * errored *after* the last time it had good data — i.e. the data on screen
 * is old and the last attempt to refresh it did not succeed. A query that
 * has never had data (first-load failure) is a hard error for the page to
 * show its own empty/error state, not a staleness notice.
 */
function isQueryFailingToRevalidate(query) {
  const state = query?.state;
  if (!state || state.data === undefined) return false;
  if (!state.error) return false;
  return (state.errorUpdatedAt || 0) > (state.dataUpdatedAt || 0);
}

/**
 * @param {import("@tanstack/react-query").QueryClient} client
 * @returns {{ lastUpdatedMs: number|null, isRevalidationFailing: boolean }}
 */
export function getMv2QueryFreshness(client) {
  const queries = client?.getQueryCache?.().getAll() || [];
  let lastUpdatedMs = 0;
  let isRevalidationFailing = false;

  for (const query of queries) {
    const dataUpdatedAt = query?.state?.dataUpdatedAt || 0;
    if (dataUpdatedAt > lastUpdatedMs) lastUpdatedMs = dataUpdatedAt;
    if (isQueryFailingToRevalidate(query)) isRevalidationFailing = true;
  }

  return {
    lastUpdatedMs: lastUpdatedMs || null,
    isRevalidationFailing,
  };
}

const snapshotCache = new WeakMap();

/**
 * Same as {@link getMv2QueryFreshness}, but returns the previous result
 * object when nothing actually changed. `useSyncExternalStore`'s getSnapshot
 * is called on every render, not just on cache events; a fresh object every
 * call would fail its `Object.is` check each time and force an extra render.
 */
export function getMv2QueryFreshnessSnapshot(client) {
  const next = getMv2QueryFreshness(client);
  const prev = snapshotCache.get(client);
  if (
    prev &&
    prev.lastUpdatedMs === next.lastUpdatedMs &&
    prev.isRevalidationFailing === next.isRevalidationFailing
  ) {
    return prev;
  }
  snapshotCache.set(client, next);
  return next;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/** Compact relative label — "just now" / "4 min ago" / "2 hr ago" / a clock time beyond that. */
export function formatMv2LastUpdated(lastUpdatedMs, nowMs) {
  if (!lastUpdatedMs) return null;
  const deltaMs = Math.max(0, nowMs - lastUpdatedMs);
  if (deltaMs < MINUTE_MS) return "just now";
  if (deltaMs < HOUR_MS) {
    const minutes = Math.floor(deltaMs / MINUTE_MS);
    return `${minutes} min ago`;
  }
  if (deltaMs < 24 * HOUR_MS) {
    const hours = Math.floor(deltaMs / HOUR_MS);
    return `${hours} hr ago`;
  }
  return new Date(lastUpdatedMs).toLocaleString();
}
